import { useState } from 'react';
import { TelegramLoginButton } from './TelegramLoginButton';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useIsMobile } from '@/hooks/use-mobile';
import { TelegramAccountStatus } from '@shared/types';
import { getTelegramStatus, unlinkTelegram } from '@/lib/api';

export function TelegramLinkButton() {
  const [showLinkWidget, setShowLinkWidget] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const { data: status, isLoading } = useQuery<TelegramAccountStatus>({
    queryKey: ['telegramStatus'],
    queryFn: getTelegramStatus,
    retry: false,
  });

  const unlinkMutation = useMutation({
    mutationFn: unlinkTelegram,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['telegramStatus'] });
      toast({
        title: 'Success',
        description: 'Telegram account unlinked successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to unlink Telegram account',
        variant: 'destructive',
      });
    },
  });

  const handleUnlink = () => {
    unlinkMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    );
  }

  if (!status?.linked) {
    return (
      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium text-muted-foreground">Telegram Account</p>
          <p className="text-sm text-muted-foreground">Not linked</p>
        </div>

        {!showLinkWidget && (
          <Button onClick={() => setShowLinkWidget(true)} variant="outline">
            Link Telegram Account
          </Button>
        )}

        {showLinkWidget && (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-muted-foreground">
              Click the button below to link your Telegram account
            </p>
            <TelegramLoginButton
              mode="link"
              buttonSize={isMobile ? 'medium' : 'large'}
              onAuth={() => {
                setShowLinkWidget(false);
                queryClient.invalidateQueries({ queryKey: ['telegramStatus'] });
              }}
            />
            <Button
              onClick={() => setShowLinkWidget(false)}
              variant="ghost"
              size="sm"
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    );
  }

  const displayName = [status.telegramFirstName, status.telegramLastName].filter(Boolean).join(' ');
  const username = status.telegramUsername || (status.telegramId ? `user${status.telegramId}` : 'unknown');

  return (
    <div className="space-y-4">
      <div>
        <p className="font-medium">Telegram Account</p>
        <p className="text-sm text-muted-foreground mt-1">
          {displayName ? `${displayName} (@${username})` : `@${username}`}
        </p>
      </div>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="destructive" size="sm">
            Unlink Telegram
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink Telegram Account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will disconnect your Telegram account from this application.
              You will no longer receive Telegram notifications or be able to log in with Telegram.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnlink}
              disabled={unlinkMutation.isPending}
            >
              {unlinkMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Unlink
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
